/**
 * MFX Quote Engine — pure, framework-free port of the validated CSR estimator.
 *
 * This is a VERBATIM port of `calc()` / `skuCostModel()` and the presets from the
 * live CSR app (csr.microflexfilm.com, index.html) — the engine that was validated
 * penny-for-penny against Randy's Master Estimate workbook (fixture M16145: base
 * $15,153.90 @ 250k). No formula was changed in the port. The DOM read/write layer
 * (getState/setState) is intentionally NOT here — the React UI owns that and calls
 * calc() with a plain QuoteState object.
 *
 * Any edit to the math in this file MUST keep quoteEngine.test.ts green.
 */

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────
export type ZipperBasis = 'M' | 'U' | 'F';                    // per 1,000 · per unit · flat/job
export type ZipperState = 'na' | 'confirmed' | 'estimated' | 'tbd';
export type EngineMode = 'pouch' | 'master';

export interface QuoteState {
  // identity / display (not used by the math, carried for the quote doc)
  company: string; attention: string; phone: string; quoteNo: string;
  estimator: string; salesRep: string; jobType: string; desc: string; freight: string;
  shape: string; process: string; machApplied: string; coating: string;
  laminate: string; zipper: string; liner: string; adhesive: string; otherMat: string;
  faceStock: string; die: string; addNotes: string;
  // geometry
  sizeAcross: number; sizeAround: number; nAcross: number; nAround: number;
  gapAcross: number; gapAround: number; cornerRadius: number; offcut: number;
  // press / plates
  colors: number; copies: number; commonPlates: number;
  costPerPlate: number; numPlates: number; costPerChange: number; numChanges: number;
  mrHours: number; mrRate: number; cuHours: number; cuRate: number;
  // material / margin
  msiCost: number; matSetup: number; stockMargin: number; mfxMU: number; ppcMU: number;
  // zipper (resolution-state aware)
  zipperCost: number; zipperBasis: ZipperBasis; zipperState: ZipperState;
  // commission (gross-up on the MFX line; off by default)
  commissionOn: boolean; commissionPct: number;
  // one-time charges (shown on quote, not part of per-tier base)
  dieCharge: number; artCharge: number; platesCharge: number; matchCharge: number;
  // quantity tiers (up to 10)
  qty: number[];
}

export interface QuoteRow {
  q: number; netFt: number; totalFt: number; stockCost: number; qtyBreak: number;
  zipper: number; base: number; mfxPreComm: number; commission: number; mfxTotal: number;
  mfxUnit: number; ppcUnit: number; ppcTotal: number; runHrs: number;
  plateChanges: number; colorChanges: number; makeReady: number; cleanUp: number;
}

export interface QuoteResult {
  repeat: number; plates: number; webWidth: number; printCyl: number;
  plateChanges: number; colorChanges: number; makeReady: number; cleanUp: number;
  rows: QuoteRow[];
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers & reference data (verbatim)
// ─────────────────────────────────────────────────────────────────────────────
export const num = (v: unknown): number => { const n = parseFloat(v as string); return isNaN(n) ? 0 : n; };

export interface Laminate { name: string; msi: number; }
export const LAMINATES: Laminate[] = [
  { name: '123CMOP — Matte .0006 Clear Lam OPP', msi: 0.044 }, { name: '127CLP — .00048 Clear Polyester Lam', msi: 0.0415 },
  { name: '166CBRL — 48G Gloss OPP for UV Lamination', msi: 0.0416 }, { name: '249SML — 1.4 Mil Matte Lam', msi: 0.176 },
  { name: '264SW — 1.5 Mil Gloss Clear Medium Coat Lam', msi: 0.180 }, { name: '290CBRL — 1 Mil Clear BOPP Raw Lam / 100 Gauge', msi: 0.0747 },
  { name: '291CBRL — 1.2 Mil Clear BOPP Raw Lam / 120 Gauge', msi: 0.0898 }, { name: '292CBRL — 2 Mil Clear BOPP Raw Lam / 200 Gauge', msi: 0.1481 },
  { name: '293CBRL — 70 Gauge Clear BOPP Raw Lam', msi: 0.0588 }, { name: '312RML — 59g Matte OPP Lamination Film', msi: 0.063 },
  { name: '322RML — .70mil Raw Matte Lamination', msi: 0.054 }, { name: '323CBRL — 200g (2mil) Clear BOPP Raw Lamination', msi: 0.192 },
  { name: '324CLP — 92g 2-Side Chem-Treated Clear Lam', msi: 0.1158 }, { name: '347CBRL — 48g Gloss OPP for UV Lamination', msi: 0.044 },
  { name: '361HSB — .8 Mil Two-Side Heat Sealable Clear OPP Lam', msi: 0.118 }, { name: '373SW — .8 mil Clear Super Thin Lamination', msi: 0.06 },
  { name: '375CBRL — 1 Mil Gloss OPP for UV Lamination', msi: 0.096 }, { name: '383CMOP — 1.2 mil Clear Matte BOPP Raw Lam', msi: 0.09 },
  { name: '399CBRL — 1.2 Mil Gloss OPP for UV Lamination', msi: 0.115 }, { name: '400RML — 70g Matte OPP Lamination Film', msi: 0.08 },
  { name: '408CMOP — 1.1 mil Matte BOPP Raw Lam', msi: 0.09 }, { name: '444STML — .7mil Soft Touch Matte Overlaminate', msi: 0.2577 },
  { name: '453STML — 1.5mil Soft Touch Matte Overlaminate', msi: 0.36326 }, { name: '464GSCL — 1.2mil Gloss Thermal Laminate', msi: 0.135 },
  { name: '465GSML — 1 mil Matte Thermal Laminating', msi: 0.10842 }, { name: '478CBRL — 75g Gloss OPP for UV Lamination', msi: 0.07 },
  { name: '484CBRL — 48g Gloss OPP Lamination', msi: 0.035 }, { name: '486CMOP — .71 mil Matte BOPP Raw Lam', msi: 0.065 },
  { name: '490SW — 1.5 mil Clear Polyester Lamination, Selfwound', msi: 0.23 }, { name: '498CPRL — 1.42 mil Clear Polyester Laminating', msi: 0.204 },
  { name: '499CBRL — 75g Gloss OPP Lamination', msi: 0.0574 }, { name: '500CBRL — 1 mil Clear BOPP Raw Lamination', msi: 0.07 },
  { name: '532CBRL — 1 Mil Gloss OPP for UV Lamination', msi: 0.07705 }, { name: '542CBRL — 1 Mil Gloss OPP Lamination', msi: 0.042 },
  { name: '543CBRL — 60g Matte OPP Lamination', msi: 0.0594 }, { name: '559CMOP — 1 Mil Matte OPP Raw Lamination', msi: 0.10 },
];

// ─────────────────────────────────────────────────────────────────────────────
// Presets (verbatim from the CSR app)
// ─────────────────────────────────────────────────────────────────────────────
export const POUCH_DEFAULTS: QuoteState = {
  company: '', attention: '', phone: '', quoteNo: '', estimator: '', salesRep: 'NONE',
  jobType: 'New', desc: '', freight: 'Riverside, California (92501)',
  shape: 'Flexible Film', sizeAcross: 0, sizeAround: 0, nAcross: 1, nAround: 1,
  gapAcross: 0, gapAround: 0, cornerRadius: 0, offcut: 0.1175,
  colors: 1, copies: 1, commonPlates: 0, process: 'Flexographic', machApplied: 'Yes',
  coating: 'NONE', laminate: '', zipper: '', liner: '0', adhesive: 'Permanent', otherMat: '0', msiCost: 0.65,
  zipperCost: 0, zipperBasis: 'M', zipperState: 'na', commissionOn: false, commissionPct: 0,
  matSetup: 3500, stockMargin: 2, mfxMU: 0, ppcMU: 0,
  mrHours: 2, mrRate: 200, cuHours: 2, cuRate: 100,
  costPerPlate: 20, numPlates: 0, costPerChange: 40, numChanges: 0,
  dieCharge: 0, artCharge: 0, platesCharge: 0, matchCharge: 0, addNotes: '',
  qty: [0, 0, 0, 0, 0], faceStock: '', die: '',
};

export const POUCH_SAMPLE: QuoteState = {
  ...POUCH_DEFAULTS,
  company: 'Developlus', attention: 'Heidi', quoteNo: 'M16145', estimator: 'Fatima Gomez',
  desc: '2.4015x6 stick pack film - Rollstock - 1 sku',
  sizeAcross: 2.4015, sizeAround: 6, nAcross: 5, nAround: 2,
  colors: 4, coating: 'UV GLOSS VARNISH', laminate: '0',
  addNotes: 'NO PLATE AND DELIVER CHARGES.',
  qty: [100000, 200000, 300000, 0, 0], faceStock: '497WPET-White Polyester',
};

export const MASTER_DEFAULTS: QuoteState = {
  ...POUCH_DEFAULTS,
  shape: 'Rectangle Label', offcut: 0, msiCost: 0.6,
  mfxMU: 0.15, costPerChange: 80, dieCharge: 200,
};

export const MASTER_SAMPLE: QuoteState = {
  ...MASTER_DEFAULTS,
  desc: '10x5 label - 4 up - rollstock',
  sizeAcross: 10, sizeAround: 5, nAcross: 4, nAround: 2,
  colors: 4, commonPlates: 3, laminate: '0', liner: '40# Liner',
  numPlates: 1, numChanges: 2,
  qty: [250000, 300000, 400000, 500000, 1000000], faceStock: '102WP-3.6mil 35#PPFP', die: 'AG1007',
};

export interface EngineModeDef { label: string; title: string; sub: string; lead: number; defaults: QuoteState; sample: QuoteState; mark: string; }
export const MODES: Record<EngineMode, EngineModeDef> = {
  pouch: { label: 'Pouch', title: 'Pouch Quote Generator', sub: 'Flexible film & pouch quote engine · matches the Master Estimate workbook', lead: 15, defaults: POUCH_DEFAULTS, sample: POUCH_SAMPLE, mark: 'M' },
  master: { label: 'Labels & Sheets', title: 'Master Quote Generator', sub: 'Labels, sheets & specialty — everything outside pouches · matches the Master Estimates Calculator', lead: 15, defaults: MASTER_DEFAULTS, sample: MASTER_SAMPLE, mark: 'L' },
};

// ─────────────────────────────────────────────────────────────────────────────
// ENGINE (mirrors Master Estimate 'Worksheet') — verbatim
// ─────────────────────────────────────────────────────────────────────────────
export function calc(s: QuoteState): QuoteResult {
  const repeat = s.sizeAround + s.gapAround;                       // B9
  const plates = Math.max(0, s.copies * s.colors - s.commonPlates); // D2
  const webWidth = (s.sizeAcross * s.nAcross + (s.nAcross - 1) * s.gapAcross + 0.875) + s.offcut; // B13
  const printCyl = repeat * s.nAround * 8;                         // B49
  const plateChanges = s.costPerPlate * s.numPlates;              // B11 = D48*D51
  const colorChanges = s.costPerChange * s.numChanges;           // B12 = D53*D54
  const makeReady = s.mrHours * s.mrRate;                        // D42
  const cleanUp = s.cuHours * s.cuRate;                          // D46
  const _zipRate = +s.zipperCost || 0, _zipState = s.zipperState || 'na', _zipBasis = s.zipperBasis || 'M';
  const zipperCostFor = (q: number): number => {
    if (!_zipRate || _zipState === 'na' || _zipState === 'tbd') return 0;
    if (_zipBasis === 'U') return _zipRate * q;
    if (_zipBasis === 'F') return _zipRate;
    return _zipRate * q / 1000;
  };
  const rows: QuoteRow[] = s.qty.map((q) => {
    const netFt = q > 0 ? (q * repeat / 12) / s.nAcross : 0;      // B14
    const totalFt = q > 0 ? (netFt * 1.2 + s.matSetup) : 0;       // B19  (netFt*0.2+netFt)+setup
    const lamMsi = (LAMINATES.find((l) => l.name === s.laminate) || { msi: 0 }).msi || 0;
    const stockCost = webWidth * totalFt * 0.012 * (s.msiCost + lamMsi); // B25
    const qtyBreak = stockCost * s.stockMargin;                  // B54
    const zipper = zipperCostFor(q);
    const base = plateChanges + colorChanges + qtyBreak + makeReady + cleanUp + zipper; // F56 (+zipper)
    const mfxPreComm = base * (1 + s.mfxMU);                     // H56 (pre-commission)
    const _commPct = (s.commissionOn && +s.commissionPct > 0) ? Math.min(0.99, +s.commissionPct / 100) : 0;
    const commission = _commPct > 0 ? mfxPreComm * (_commPct / (1 - _commPct)) : 0; // rep cut, grossed up
    const mfxTotal = mfxPreComm + commission;                   // H56 grossed to preserve margin
    const mfxUnit = q > 0 ? mfxTotal / q : NaN;                  // I56
    const ppcUnit = q > 0 ? mfxUnit * (1 + s.ppcMU) : NaN;       // E61
    const ppcTotal = q > 0 ? ppcUnit * q : NaN;                 // F61
    const runHrs = q > 0 ? ((totalFt / 150) / 60) + (s.numChanges * 0.4) + (s.numPlates * 0.25) + (s.colors * 0.41) : NaN; // J56
    return { q, netFt, totalFt, stockCost, qtyBreak, zipper, base, mfxPreComm, commission, mfxTotal, mfxUnit, ppcUnit, ppcTotal, runHrs, plateChanges, colorChanges, makeReady, cleanUp };
  });
  return { repeat, plates, webWidth, printCyl, plateChanges, colorChanges, makeReady, cleanUp, rows };
}

// ─────────────────────────────────────────────────────────────────────────────
// SKU × Quantity price matrix — verbatim (decompose into fixed + variable)
// ─────────────────────────────────────────────────────────────────────────────
export interface SkuCostModel { setupPerSku: number; costPerUnit: number; }
export function skuCostModel(s: QuoteState, r: QuoteResult): SkuCostModel {
  const stockFix = r.webWidth * 0.012 * s.msiCost * s.matSetup;                    // fixed stock cost from material set-up
  const stockVar = r.webWidth * 0.012 * s.msiCost * 1.2 * r.repeat / (12 * s.nAcross); // stock cost per finished unit
  const _cp = (s.commissionOn && +s.commissionPct > 0) ? Math.min(0.99, +s.commissionPct / 100) : 0;
  const _cf = _cp > 0 ? 1 / (1 - _cp) : 1;
  const setupPerSku = _cf * (1 + s.mfxMU) * (r.plateChanges + r.colorChanges + r.makeReady + r.cleanUp + s.stockMargin * stockFix);
  const costPerUnit = _cf * (1 + s.mfxMU) * s.stockMargin * stockVar;
  return { setupPerSku, costPerUnit };
}
export function skuCellUnit(N: number, Q: number, m: SkuCostModel | null): number {
  return (m && Q > 0) ? (N * m.setupPerSku + m.costPerUnit * Q) / Q : NaN;
}

// ─────────────────────────────────────────────────────────────────────────────
// Dropdown option lists (verbatim from the CSR app selects)
// ─────────────────────────────────────────────────────────────────────────────
export const SHAPES: string[] = [
  'Flexible Film', 'Flexible Film Pouch', 'Flexible Film Stick Pack',
  'Rectangle Label', 'Circle Label', 'Oval Label', 'Special Shape Label', 'Sheets',
];
export const COATINGS: string[] = [
  'NONE', 'GLOSS VARNISH-WB', 'UV GLOSS VARNISH', 'UV MATTE VARNISH', 'UV GLOSS VARNISH-FGP',
  'UV GLOSS VARNISH-DBV', 'SPOT UV GLOSS VARNISH', 'SPOT UV MATTE VARNISH', 'SPOT FDA APPROVED VARNISH',
  'SPOT MATTE VARNISH- DBV', 'FDA APPROVED VARNISH-DBG', 'FDA APPROVED VARNISH-DBM', 'FDA APPROVED VARNISH',
  'Matte Varnish-DBV', 'FDA APPROVED VARNISH/MATTE', 'Thermal Imp. Varnish-WB', 'CCM134-Matte Varnish',
  'GLOSS VARNISH-DBV', 'UV GLOSS VARNISH-DBV1', 'Soft Touch-DBV', 'SPOT UV VARNISH/SOFT TOUCH MATTE',
  'UV Thermal Varnish', 'CCM200 / Matte Varnish', 'CCM200 / CCM300 Gloss', 'CCM200 / UV Gloss Varnish',
  'CCM110-Matte/SPT Gloss UV Varn.', 'Matte /Soft Touch- DBV', 'Satin Varnish', 'Spot Chrome Silver',
];
export const ADHESIVES: string[] = ['None', 'All Temp', 'Removable', 'Permanent', 'All Temp Permanent', 'Cold Temp Permanent', 'Ultra Removable'];
export const PROCESSES: string[] = ['Flexographic', 'Digital'];
export const MACH_APPLIED: string[] = ['Yes', 'No'];
export const JOB_TYPES: string[] = ['New', 'Repeat', 'Revision'];
export const laminateNames = (): string[] => LAMINATES.map((l) => l.name);

// Money / unit formatters (match the CSR app's display)
export const money = (v: number): string => (!isFinite(v)) ? '—' : '$' + v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
export const unitMoney = (v: number): string => (!isFinite(v) || v <= 0) ? '—' : '$' + v.toLocaleString('en-US', { minimumFractionDigits: 4, maximumFractionDigits: 5 });
export const intfmt = (v: number): string => (!isFinite(v)) ? '—' : Math.round(v).toLocaleString('en-US');

// Build a QuoteState from a partial (fills every field from POUCH_DEFAULTS, pads qty to 10)
export function normalizeState(partial: Partial<QuoteState>): QuoteState {
  const base: QuoteState = { ...POUCH_DEFAULTS, ...partial } as QuoteState;
  const q = (partial.qty ?? base.qty ?? []).slice(0, 10);
  base.qty = [...q, ...Array(Math.max(0, 10 - q.length)).fill(0)];
  return base;
}
